import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, orderBy, limit, doc, updateDoc } from 'firebase/firestore';
import { timeAgo } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { useThemeColors } from '../context/ThemeContext';
import {
  Home, Car, Calendar, Sparkles, Wrench, LifeBuoy, ShoppingCart, Package,
  Bell, MessageCircle, User, Settings, HelpCircle, CreditCard, Users, Star,
  TrendingUp, LayoutDashboard, Store, Shield, LogOut, BarChart3, Eye, FileText, Globe, Megaphone,
  Repeat, Check, Trophy, Tag, ClipboardCheck, BadgeCheck, Activity, Crown, ShieldCheck
} from 'lucide-react';
import { EMERGENCIAS_ACTIVAS } from '../utils/featureFlags';
import { cloudinaryImageUrl } from '../utils/cloudinaryImage';

export default function Sidebar() {
  const { t } = useTranslation();
  const { userProfile, logout, effectiveRole, availableRoles, setActiveRole } = useAuth();
  const [showRoleSwitch, setShowRoleSwitch] = useState(false);
  const { getItemCount } = useCart();
  const { isDark } = useThemeColors();
  const tokens = isDark ? dsv2 : dsv2Light;
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const popoverRef = useRef(null);
  const bellButtonRef = useRef(null);

  useEffect(() => {
    if (!auth.currentUser) return;
    const qN = query(
      collection(db, 'notifications'),
      where('userId', '==', auth.currentUser.uid),
      where('read', '==', false)
    );
    const uN = onSnapshot(qN, (snap) => setUnread(snap.size), (err) => { if (err?.code !== 'permission-denied') console.error('Sidebar notif snapshot:', err); });

    const qM = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', auth.currentUser.uid)
    );
    const uM = onSnapshot(qM, (snap) => {
      const count = snap.docs.filter(d => (d.data().unreadCounts?.[auth.currentUser.uid] || 0) > 0).length;
      setMsgUnread(count);
    }, (err) => { if (err?.code !== 'permission-denied') console.error('Sidebar msg snapshot:', err); });

    return () => { uN(); uM(); };
  }, []);

  useEffect(() => {
    if (!showNotifs || !auth.currentUser) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', auth.currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
    const u = onSnapshot(q, snap => setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() }))), (err) => { if (err?.code !== 'permission-denied') console.error('Sidebar notiflist snapshot:', err); });
    return u;
  }, [showNotifs]);

  useEffect(() => {
    if (!showNotifs) return;
    const handle = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (bellButtonRef.current?.contains(e.target)) return;
      setShowNotifs(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showNotifs]);

  const markAsRead = async (id) => {
    await updateDoc(doc(db, 'notifications', id), { read: true, readAt: new Date() }).catch(() => {});
  };
  const markAllRead = async () => {
    await Promise.all(notifications.filter(n => !n.read).map(n =>
      updateDoc(doc(db, 'notifications', n.id), { read: true, readAt: new Date() }).catch(() => {}),
    ));
  };
  const handleNotifClick = async (n) => {
    await markAsRead(n.id);
    setShowNotifs(false);
    if (n.link) navigate(n.link);
  };

  const role = effectiveRole;
  const cartCount = getItemCount();

  const handleLogout = async () => {
    try { await logout(); navigate('/'); } catch (e) {}
  };

  const CLIENT_SECTIONS = [
    {
      title: t('sidebar.sectionMain'),
      items: [
        { Icon: Home, label: t('nav.home'), path: '/home' },
        { Icon: Car, label: t('nav.vehicles'), path: '/vehicles', match: ['/vehicles', '/vehicle/'] },
        { Icon: Activity, label: t('nav.health'), path: '/dashboard/stats' },
        { Icon: Calendar, label: t('nav.myAppointments'), path: '/appointments', match: ['/appointments', '/appointment/'] },
        { Icon: Sparkles, label: t('sidebar.diagnoseAI'), path: '/diagnose' },
      ],
    },
    {
      title: t('sidebar.sectionServices'),
      items: [
        { Icon: Wrench, label: t('nav.mechanics'), path: '/mechanics', match: ['/mechanics', '/mechanic/'] },
        { Icon: LifeBuoy, label: t('nav.services'), path: '/asistencia', match: ['/asistencia', '/emergency-request'] },
        { Icon: Store, label: t('nav.marketplace'), path: '/marketplace', match: ['/marketplace', '/product/', '/cart', '/checkout'] },
        { Icon: Tag, label: t('nav.autos'), path: '/autos', match: ['/autos', '/mis-anuncios'] },
        { Icon: Package, label: t('nav.myOrders'), path: '/my-orders' },
      ],
    },
    {
      title: t('sidebar.sectionAccount'),
      items: [
        { Icon: Bell, label: t('inbox.title'), path: '/inbox', badge: unread },
        { Icon: MessageCircle, label: t('nav.messages'), path: '/messages', badge: msgUnread },
        { Icon: Crown, label: t('clientProBilling.navTitle'), path: '/pro' },
        { Icon: ShoppingCart, label: t('nav.myCart'), path: '/cart', badge: cartCount },
        { Icon: User, label: t('sidebar.myProfile'), path: '/dashboard' },
        { Icon: Settings, label: t('nav.settings'), path: '/profile/edit' },
        { Icon: HelpCircle, label: t('nav.help'), path: '/help' },
      ],
    },
  ];

  const MECHANIC_SECTIONS = [
    {
      title: t('sidebar.sectionMechanic'),
      items: [
        { Icon: LayoutDashboard, label: t('sidebar.myDashboard'), path: '/mechanic/dashboard' },
        { Icon: Calendar, label: t('nav.myAppointments'), path: '/mechanic/appointments', match: ['/mechanic/appointments', '/mechanic/appointment/'] },
        { Icon: FileText, label: t('nav.clientCases'), path: '/mechanic/service-history' },
        { Icon: Wrench, label: t('nav.myWorkshop'), path: '/mechanic/setup' },
        ...(EMERGENCIAS_ACTIVAS ? [{ Icon: LifeBuoy, label: t('nav.assistance'), path: '/provider/mode' }] : []),
      ],
    },
    {
      title: t('sidebar.sectionServices'),
      items: [
        { Icon: Car, label: t('nav.vehicles'), path: '/vehicles', match: ['/vehicles', '/vehicle/'] },
        { Icon: Calendar, label: t('nav.myAppointments'), path: '/appointments', match: ['/appointments', '/appointment/'] },
        { Icon: Store, label: t('nav.marketplace'), path: '/marketplace', match: ['/marketplace', '/product/', '/cart', '/checkout'] },
        { Icon: Tag, label: t('nav.autos'), path: '/autos', match: ['/autos', '/mis-anuncios'] },
        { Icon: ClipboardCheck, label: t('nav.vehicleInspections'), path: '/workshop/verifications', match: ['/workshop/verifications', '/autos/verificar/'] },
        { Icon: BadgeCheck, label: t('certService.navTitle'), path: '/workshop/register-service', match: ['/workshop/register-service'] },
        { Icon: Package, label: t('nav.myOrders'), path: '/my-orders' },
        { Icon: Sparkles, label: t('sidebar.diagnoseAI'), path: '/diagnose' },
        { Icon: Wrench, label: t('nav.mechanics'), path: '/mechanics', match: ['/mechanics', '/mechanic/'] },
        { Icon: ShoppingCart, label: t('nav.myCart'), path: '/cart', badge: cartCount },
        { Icon: Trophy, label: t('nav.rankings'), path: '/rankings' },
      ],
    },
    {
      title: t('sidebar.sectionAccount'),
      items: [
        { Icon: Bell, label: t('inbox.title'), path: '/inbox', badge: unread },
        { Icon: MessageCircle, label: t('nav.messages'), path: '/messages', badge: msgUnread },
        { Icon: CreditCard, label: t('mechBilling.title'), path: '/mechanic/billing' },
        { Icon: FileText, label: t('nav.clientCases'), path: '/my-cases' },
        { Icon: User, label: t('sidebar.myProfile'), path: '/dashboard' },
        { Icon: Settings, label: t('nav.settings'), path: '/profile/edit' },
        { Icon: HelpCircle, label: t('nav.help'), path: '/help' },
      ],
    },
  ];

  const WORKSHOP_SECTIONS = [
    {
      title: t('sidebar.sectionWorkshop'),
      items: [
        { Icon: LayoutDashboard, label: t('sidebar.myDashboard'), path: '/workshop/dashboard' },
        { Icon: Calendar, label: t('nav.myAppointments'), path: '/workshop/appointments' },
        { Icon: Package, label: t('nav.workshopOrders'), path: '/workshop/orders' },
        { Icon: Package, label: t('nav.myProducts'), path: '/workshop/products', match: ['/workshop/products', '/workshop/store'] },
        { Icon: Star, label: t('sidebar.reviews'), path: '/workshop/reviews' },
        // 🔴 EL SELLO, EN LA SECCIÓN PRINCIPAL DEL NEGOCIO. Hasta hoy su único
        // enlace era una tarjeta escondida tras «Más opciones» en el panel, y
        // por eso `workshop_business_doc` no tenía ni una fila en producción.
        { Icon: ShieldCheck, label: t('nav.businessVerification'), path: '/workshop/verification' },
      ],
    },
    {
      title: t('sidebar.sectionServices'),
      items: [
        { Icon: TrendingUp, label: t('sidebar.earnings'), path: '/workshop/earnings' },
        { Icon: Eye, label: t('sidebar.profileViews'), path: '/workshop/profile-views' },
        { Icon: Users, label: t('sidebar.clients'), path: '/workshop/clients' },
        ...(EMERGENCIAS_ACTIVAS ? [{ Icon: LifeBuoy, label: t('nav.assistance'), path: '/provider/mode' }] : []),
        { Icon: Store, label: t('nav.marketplace'), path: '/marketplace', match: ['/marketplace', '/product/', '/cart', '/checkout'] },
        { Icon: Tag, label: t('nav.autos'), path: '/autos', match: ['/autos', '/mis-anuncios'] },
        { Icon: ClipboardCheck, label: t('nav.vehicleInspections'), path: '/workshop/verifications', match: ['/workshop/verifications', '/autos/verificar/'] },
        { Icon: BadgeCheck, label: t('certService.navTitle'), path: '/workshop/register-service', match: ['/workshop/register-service'] },
        { Icon: Car, label: t('nav.vehicles'), path: '/vehicles', match: ['/vehicles', '/vehicle/'] },
        { Icon: Calendar, label: t('nav.myAppointments'), path: '/appointments', match: ['/appointments', '/appointment/'] },
        { Icon: Package, label: t('nav.myOrders'), path: '/my-orders' },
        { Icon: Sparkles, label: t('sidebar.diagnoseAI'), path: '/diagnose' },
        { Icon: Wrench, label: t('nav.mechanics'), path: '/mechanics', match: ['/mechanics', '/mechanic/'] },
        { Icon: ShoppingCart, label: t('nav.myCart'), path: '/cart', badge: cartCount },
        { Icon: Trophy, label: t('nav.rankings'), path: '/rankings' },
      ],
    },
    {
      title: t('sidebar.sectionAccount'),
      items: [
        { Icon: Bell, label: t('inbox.title'), path: '/inbox', badge: unread },
        { Icon: MessageCircle, label: t('nav.messages'), path: '/messages', badge: msgUnread },
        { Icon: CreditCard, label: t('sidebar.subscription'), path: '/workshop/billing' },
        { Icon: FileText, label: t('nav.clientCases'), path: '/my-cases' },
        { Icon: User, label: t('sidebar.myProfile'), path: '/dashboard' },
        { Icon: Settings, label: t('nav.settings'), path: '/profile/edit' },
        { Icon: HelpCircle, label: t('nav.help'), path: '/help' },
      ],
    },
  ];

  const ADMIN_SECTIONS = [
    {
      title: t('sidebar.sectionAdmin'),
      items: [
        { Icon: BarChart3, label: t('admin.tabStats'), path: '/admin/dashboard' },
        { Icon: FileText, label: t('admin.tabAppeals'), path: '/admin/dashboard?tab=appeals' },
        { Icon: TrendingUp, label: t('admin.bizTab'), path: '/admin/dashboard?tab=negocio' },
        { Icon: Users, label: t('admin.tabUsers'), path: '/admin/dashboard?tab=usuarios' },
        { Icon: Megaphone, label: t('admin.tabComm'), path: '/admin/dashboard?tab=comunicacion' },
        { Icon: Wrench, label: t('admin.tabTools'), path: '/admin/dashboard?tab=herramientas' },
      ],
    },
    {
      title: t('admin.tabTools'),
      items: [
        { Icon: Shield, label: t('admin.toolKyc'), path: '/admin/kyc' },
        { Icon: Store, label: t('admin.toolWorkshopKyc'), path: '/admin/workshop-kyc' },
        { Icon: Package, label: t('admin.toolOrders'), path: '/admin/orders' },
        { Icon: Star, label: t('admin.toolReviews'), path: '/admin/reviews' },
        { Icon: LifeBuoy, label: t('admin.toolEmergencies'), path: '/admin/emergencies' },
        { Icon: HelpCircle, label: t('admin.toolSupport'), path: '/admin/support' },
        { Icon: BarChart3, label: t('admin.toolAnalytics'), path: '/admin/analytics' },
        { Icon: Package, label: t('admin.toolProducts'), path: '/admin/products' },
        { Icon: Megaphone, label: t('admin.toolAnnouncements'), path: '/admin/announcements' },
        { Icon: FileText, label: t('admin.toolAudit'), path: '/admin/audit' },
      ],
    },
    {
      title: t('sidebar.sectionAccount'),
      items: [
        { Icon: Bell, label: t('inbox.title'), path: '/inbox', badge: unread },
        { Icon: MessageCircle, label: t('nav.messages'), path: '/messages', badge: msgUnread },
        { Icon: User, label: t('sidebar.myProfile'), path: '/dashboard' },
        { Icon: Settings, label: t('nav.settings'), path: '/profile/edit' },
      ],
    },
  ];

  const sections = role === 'admin' ? ADMIN_SECTIONS
    : role === 'workshop_owner' ? WORKSHOP_SECTIONS
    : role === 'mechanic' ? MECHANIC_SECTIONS
    : CLIENT_SECTIONS;

  const isActive = (item) => {
    const paths = item.match || [item.path];
    return paths.some(p => p.endsWith('/') ? location.pathname.startsWith(p) : location.pathname === p);
  };

  const userName = userProfile?.fullName || auth.currentUser?.displayName || 'Usuario';
  const initials = (userProfile?.fullName || auth.currentUser?.email || 'U')
    .split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  const photoURL = userProfile?.photoURL || auth.currentUser?.photoURL;
  const labelForRole = (r) => r === 'workshop_owner' ? t('nav.workshop')
    : r === 'mechanic' ? t('profile.role.mechanic')
    : r === 'admin' ? t('sidebar.sectionAdmin')
    : t('profile.role.client');
  const homeForRole = (r) => r === 'workshop_owner' ? '/workshop/dashboard'
    : r === 'mechanic' ? '/mechanic/dashboard'
    : r === 'admin' ? '/admin/dashboard'
    : '/home';
  const roleLabel = labelForRole(role);
  const canSwitchRole = availableRoles.length > 1;

  const switchTo = (r) => {
    setActiveRole(r);
    setShowRoleSwitch(false);
    navigate(homeForRole(r));
  };

  const sidebarBg = isDark ? dsv2.surfaceLow : dsv2Light.surface;
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : dsv2Light.surfaceLow;
  const logoutHoverBg = isDark ? 'rgba(255,255,255,.06)' : dsv2Light.surfaceLow;
  const logoSrc = '/mekafix-logo-v3.png';

  return (
    <aside style={{
      width: tokens.space.sidebar,
      minWidth: tokens.space.sidebar,
      backgroundColor: sidebarBg,
      color: tokens.text,
      fontFamily: tokens.font.body,
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      position: 'sticky',
      top: 0,
      left: 0,
      zIndex: 100,
      borderRight: '1px solid rgba(230,33,23,0.45)',
      boxShadow: '1px 0 0 rgba(230,33,23,0.45), 8px 0 32px rgba(0,0,0,0.4)',
    }}>
      {/* Brand */}
      <div
        style={{
          padding: '18px 18px 16px',
          borderBottom: '1px solid rgba(230,33,23,0.35)',
          boxShadow: '0 1px 0 rgba(230,33,23,0.32), 0 0 12px rgba(230,33,23,0.18)',
          position: 'relative',
        }}
      >
        <div onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
          <img
            src={logoSrc}
            alt="MekaFix"
            style={{ width: '100%', height: 'auto', display: 'block', flexShrink: 0 }}
          />
          <div style={{
            fontFamily: tokens.font.mono, fontSize: 9, color: tokens.textDim,
            textTransform: 'uppercase', letterSpacing: '0.18em',
            marginTop: 8, textAlign: 'center',
          }}>Precision Care</div>
        </div>
      </div>

      {/* Sections */}
      {/* 🔴 EL ÚLTIMO ENLACE DEL MENÚ SALÍA PARTIDO POR LA MITAD.
          A 1280×800 la lista no cabe entera y esta caja recorta por donde toque:
          medido, el corte caía sobre «MekaFix Pro», o sea el enlace al producto
          de pago, que quedaba ilegible y a medio pulsar. `minHeight: 0` es lo
          que hace que un hijo `flex:1` pueda ENCOGER dentro de una columna flex
          (sin él el mínimo es su contenido y la caja empuja al pie fuera de la
          pantalla en vez de desplazarse), y el relleno de abajo garantiza que el
          último enlace se pueda llevar entero al final del recorrido en vez de
          quedarse pegado al borde del recorte. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: 6, paddingBottom: 12 }}>
        {sections.map((section, sIdx) => (
          <div key={sIdx} style={{ padding: '12px 10px 4px' }}>
            <div style={{
              padding: '6px 14px',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: tokens.textDim,
              fontFamily: tokens.font.mono,
              fontWeight: 700,
            }}>
              {section.title}
            </div>
            {section.items.map((item, iIdx) => {
              const active = isActive(item);
              const Icon = item.Icon;
              const activeBg = 'linear-gradient(90deg, rgba(230,33,23,0.20) 0%, rgba(230,33,23,0.08) 50%, transparent 100%)';
              return (
                <div
                  key={iIdx}
                  onClick={() => navigate(item.path)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '8px 14px',
                    fontSize: 13.5,
                    fontFamily: tokens.font.body,
                    color: active ? tokens.red : tokens.textMuted,
                    background: active ? activeBg : 'transparent',
                    borderRadius: tokens.radius.md,
                    margin: '1px 0',
                    fontWeight: 500,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background .15s, color .15s',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = hoverBg; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <Icon size={18} strokeWidth={1.75} style={{ color: active ? tokens.red : tokens.textMuted, flexShrink: 0 }} />
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                  {item.badge > 0 && (
                    <span style={{
                      background: tokens.red,
                      color: '#fff',
                      borderRadius: tokens.radius.pill,
                      fontSize: 10.5,
                      padding: '2px 7px',
                      fontFamily: tokens.font.mono,
                      fontWeight: 700,
                      lineHeight: 1.4,
                    }}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Página principal (sitio público) */}
      <div style={{ padding: '8px 10px 0' }}>
        <div
          onClick={() => navigate('/')}
          style={{
            display: 'flex', alignItems: 'center', gap: 11,
            padding: '8px 14px',
            fontSize: 13.5,
            fontFamily: tokens.font.body,
            color: tokens.textMuted,
            borderRadius: tokens.radius.md,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background .15s, color .15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Globe size={18} strokeWidth={1.75} style={{ color: tokens.textMuted, flexShrink: 0 }} />
          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('sidebar.publicSite')}</span>
        </div>
      </div>

      {/* Selector de contexto (cuenta unificada) */}
      {canSwitchRole && (
        <div style={{ padding: '4px 10px 0' }}>
          {showRoleSwitch && (
            <div style={{ background: tokens.surface, border: `1px solid ${tokens.outline}`, borderRadius: tokens.radius.md, padding: 4, marginBottom: 6 }}>
              {availableRoles.map(r => (
                <div
                  key={r}
                  onClick={() => switchTo(r)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    fontSize: 13, color: r === role ? tokens.red : tokens.textMuted,
                    borderRadius: tokens.radius.sm, cursor: 'pointer', fontWeight: r === role ? 700 : 500,
                  }}
                  onMouseEnter={(e) => { if (r !== role) e.currentTarget.style.background = hoverBg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{labelForRole(r)}</span>
                  {r === role && <Check size={14} color={tokens.red} />}
                </div>
              ))}
            </div>
          )}
          <div
            onClick={() => setShowRoleSwitch(s => !s)}
            style={{
              display: 'flex', alignItems: 'center', gap: 11, padding: '8px 14px',
              fontSize: 13.5, fontFamily: tokens.font.body, color: tokens.textMuted,
              borderRadius: tokens.radius.md, fontWeight: 500, cursor: 'pointer',
              transition: 'background .15s, color .15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <Repeat size={18} strokeWidth={1.75} style={{ color: tokens.textMuted, flexShrink: 0 }} />
            <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('sidebar.switchContext')}</span>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        padding: '12px 14px',
        borderTop: '1px solid rgba(230,33,23,0.35)',
        boxShadow: '0 -1px 0 rgba(230,33,23,0.32), 0 0 12px rgba(230,33,23,0.18)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: photoURL ? 'transparent' : `linear-gradient(135deg, #FCA5A5, ${tokens.red})`,
          display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700, fontSize: 12,
          overflow: 'hidden', flexShrink: 0,
        }}>
          {photoURL
            ? <img src={cloudinaryImageUrl(photoURL, 96)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : initials}
        </div>
        <div style={{ flex: 1, fontSize: 12.5, minWidth: 0 }}>
          <div style={{ color: tokens.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
          <div style={{
            color: tokens.textDim,
            fontFamily: tokens.font.mono,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginTop: 1,
          }}>{roleLabel}</div>
        </div>
        <button
          onClick={handleLogout}
          title={t('profile.logout')}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: tokens.textMuted, padding: 6, borderRadius: 6,
            display: 'grid', placeItems: 'center',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = tokens.text; e.currentTarget.style.background = logoutHoverBg; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = tokens.textMuted; e.currentTarget.style.background = 'transparent'; }}
        >
          <LogOut size={16} strokeWidth={1.75} />
        </button>
      </div>

      {showNotifs && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            left: tokens.space.sidebar + 8,
            top: 80,
            width: 380,
            maxHeight: '70vh',
            background: tokens.surface,
            border: `1px solid ${tokens.outline}`,
            borderRadius: tokens.radius.lg,
            boxShadow: '0 12px 36px rgba(0,0,0,.35), 0 0 24px rgba(230,33,23,0.18)',
            zIndex: 200,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: tokens.font.body,
          }}
        >
          <div style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${tokens.outline}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: tokens.text, fontFamily: tokens.font.head }}>
              {t('inbox.title', 'Notificaciones')}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {notifications.some(n => !n.read) && (
                <button
                  onClick={markAllRead}
                  style={{
                    padding: '4px 10px', background: 'none', border: 'none',
                    color: tokens.red, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {t('inbox.markAllRead', 'Marcar todas')}
                </button>
              )}
              <button
                onClick={() => { setShowNotifs(false); navigate('/inbox'); }}
                style={{
                  padding: '4px 10px', background: tokens.surfaceLow,
                  border: 'none', borderRadius: tokens.radius.md,
                  color: tokens.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('home.viewAll', 'Ver todas')}
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: tokens.textMuted }}>
                <Bell size={32} color={tokens.textDim} style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 14 }}>{t('home.noNotifications', 'Sin notificaciones')}</p>
              </div>
            ) : notifications.slice(0, 12).map(n => (
              <div
                key={n.id}
                onClick={() => handleNotifClick(n)}
                style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${tokens.outline}`,
                  cursor: 'pointer',
                  background: n.read ? tokens.surface : tokens.surfaceLow,
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = tokens.surfaceHigh)}
                onMouseLeave={(e) => (e.currentTarget.style.background = n.read ? tokens.surface : tokens.surfaceLow)}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: tokens.radius.md,
                  background: n.read ? tokens.surfaceLow : 'rgba(230,33,23,0.12)',
                  border: `1px solid ${n.read ? tokens.outline : 'rgba(230,33,23,0.3)'}`,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>
                  <Bell size={14} color={n.read ? tokens.textMuted : tokens.red} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: n.read ? 400 : 600, color: tokens.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{n.title}</div>
                  <div style={{
                    fontSize: 12, color: tokens.textMuted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{n.body || n.message}</div>
                  <div style={{
                    fontSize: 10.5, color: tokens.textDim, marginTop: 2,
                    fontFamily: tokens.font.mono,
                  }}>{n.createdAt?.toDate?.() ? timeAgo(n.createdAt.toDate()) : ''}</div>
                </div>
                {!n.read && (
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: tokens.red, flexShrink: 0, marginTop: 6,
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
